import { useRef, useState } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { Mail, Phone, MapPin, Award, Send, CheckCircle, MessageSquare } from 'lucide-react'

export default function Kontakt() {
  const heroRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll()
  const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0])
  const heroScale = useTransform(scrollYProgress, [0, 0.15], [1, 1.1])

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    anlass: '',
    personenanzahl: '',
    telefon: '',
    terminwunsch: '',
    nachricht: '',
    datenschutz: false,
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked
      setFormData(prev => ({ ...prev, [name]: checked }))
    } else {
      setFormData(prev => ({ ...prev, [name]: value }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus('idle')

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Fehler beim Versenden der Nachricht')
      }

      // Erfolg
      setSubmitStatus('success')
      setFormData({
        name: '',
        email: '',
        anlass: '',
        personenanzahl: '',
        telefon: '',
        terminwunsch: '',
        nachricht: '',
        datenschutz: false,
      })
    } catch (error) {
      console.error('Fehler beim Versenden:', error)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="bg-[#0a0a0a]">
      
      {/* ════════════════════════════════════════════════════════════
          HERO SECTION
      ════════════════════════════════════════════════════════════ */}
      <section ref={heroRef} className="relative h-[50vh] min-h-[400px] max-h-[550px] w-full overflow-hidden">
        <motion.div style={{ scale: heroScale }} className="absolute inset-0">
          <img
            src="/assets/heros/hero_bilder.jpg"
            alt="Kontakt"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'brightness(0.3)' }}
          />
        </motion.div>

        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-[#0a0a0a]" />
        <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-brand-800/10 rounded-full blur-[120px]" />
        
        <motion.div 
          style={{ opacity: heroOpacity }}
          className="relative z-10 h-full flex flex-col justify-center items-center text-center px-6 pt-16"
        >
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-4"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full">
              <MessageSquare className="w-3 h-3 text-brand-400" />
              <span className="text-white/70 text-xs uppercase tracking-[0.25em]">Schreiben Sie uns</span>
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white font-light tracking-tight leading-[1.1] mb-4"
          >
            Kontakt & <span className="italic text-white/70">Reservierung</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="text-white/50 text-xs sm:text-sm md:text-base max-w-xl leading-relaxed px-4"
          >
            Wir melden uns schnellstmöglich bei Ihnen zurück.
          </motion.p>
        </motion.div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          CONTACT CONTENT
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-24 md:py-32">
        <div className="absolute top-40 right-20 w-[500px] h-[500px] bg-brand-800/5 rounded-full blur-[120px]" />
        
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-5 gap-8 md:gap-12 lg:gap-16">
            
            {/* Left Column: Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Required Info Card */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="p-6 bg-white/[0.02] border border-white/5 rounded-2xl"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Award className="w-5 h-5 text-brand-400" />
                  <h3 className="font-serif text-lg text-white">Benötigte Informationen</h3>
                </div>
                <ul className="space-y-3">
                  {['Ihre Telefonnummer', 'Anlass der Veranstaltung', 'Ca. Personenanzahl', 'Details soweit bekannt'].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-white/50 text-sm">
                      <CheckCircle className="w-4 h-4 text-brand-400 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Contact Info Card */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="p-6 bg-white/[0.02] border border-white/5 rounded-2xl space-y-6"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-brand-800/10 border border-brand-800/20 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-brand-400" />
                  </div>
                  <div>
                    <p className="text-brand-400/60 text-xs uppercase tracking-wider mb-1">Telefon</p>
                    <a href="tel:+4915755537615" className="text-white hover:text-brand-400 transition-colors font-medium">
                      01575 5537615
                    </a>
                  </div>
                </div>

                <div className="h-px bg-white/5" />

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-brand-800/10 border border-brand-800/20 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-brand-400" />
                  </div>
                  <div>
                    <p className="text-brand-400/60 text-xs uppercase tracking-wider mb-1">E-Mail</p>
                    <a href="mailto:info@hueckels-may.de" className="text-white hover:text-brand-400 transition-colors font-medium">
                      info@hueckels-may.de
                    </a>
                  </div>
                </div>

                <div className="h-px bg-white/5" />

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-brand-800/10 border border-brand-800/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-brand-400" />
                  </div>
                  <div>
                    <p className="text-brand-400/60 text-xs uppercase tracking-wider mb-1">Adresse</p>
                    <p className="text-white">Hückelsmaystr. 70<br />47804 Krefeld</p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Right Column: Form */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-3"
            >
              <div className="p-8 bg-white/[0.02] border border-white/5 rounded-2xl">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm text-white/70 mb-2">
                      Name <span className="text-brand-400">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white text-sm md:text-base focus:border-brand-800 focus:ring-1 focus:ring-brand-800 transition-all"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm text-white/70 mb-2">
                      E-Mail <span className="text-brand-400">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white text-sm md:text-base focus:border-brand-800 focus:ring-1 focus:ring-brand-800 transition-all"
                    />
                  </div>

                  {/* Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                    <div>
                      <label htmlFor="anlass" className="block text-sm text-white/70 mb-2">
                        Anlass <span className="text-brand-400">*</span>
                      </label>
                      <input
                        type="text"
                        id="anlass"
                        name="anlass"
                        required
                        value={formData.anlass}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white text-sm md:text-base focus:border-brand-800 focus:ring-1 focus:ring-brand-800 transition-all"
                      />
                    </div>

                    <div>
                      <label htmlFor="personenanzahl" className="block text-sm text-white/70 mb-2">
                        Personenanzahl <span className="text-brand-400">*</span>
                      </label>
                      <input
                        type="number"
                        id="personenanzahl"
                        name="personenanzahl"
                        required
                        min="1"
                        value={formData.personenanzahl}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white text-sm md:text-base focus:border-brand-800 focus:ring-1 focus:ring-brand-800 transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                    <div>
                      <label htmlFor="telefon" className="block text-sm text-white/70 mb-2">
                        Telefon <span className="text-brand-400">*</span>
                      </label>
                      <input
                        type="tel"
                        id="telefon"
                        name="telefon"
                        required
                        value={formData.telefon}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white text-sm md:text-base focus:border-brand-800 focus:ring-1 focus:ring-brand-800 transition-all"
                      />
                    </div>

                    <div>
                      <label htmlFor="terminwunsch" className="block text-sm text-white/70 mb-2">
                        Terminwunsch <span className="text-brand-400">*</span>
                      </label>
                      <input
                        type="date"
                        id="terminwunsch"
                        name="terminwunsch"
                        required
                        value={formData.terminwunsch}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white text-sm md:text-base focus:border-brand-800 focus:ring-1 focus:ring-brand-800 transition-all"
                      />
                    </div>
                  </div>

                  {/* Nachricht */}
                  <div>
                    <label htmlFor="nachricht" className="block text-sm text-white/70 mb-2">
                      Nachricht <span className="text-brand-400">*</span>
                    </label>
                    <textarea
                      id="nachricht"
                      name="nachricht"
                      required
                      rows={5}
                      value={formData.nachricht}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white focus:border-brand-800 focus:ring-1 focus:ring-brand-800 transition-all resize-y"
                    />
                  </div>

                  {/* Datenschutz */}
                  <div className="flex items-start gap-3 p-4 bg-white/[0.02] border border-white/5 rounded-xl">
                    <input
                      type="checkbox"
                      id="datenschutz"
                      name="datenschutz"
                      required
                      checked={formData.datenschutz}
                      onChange={handleChange}
                      className="mt-1 w-4 h-4 text-brand-800 border-white/20 rounded focus:ring-brand-800"
                    />
                    <label htmlFor="datenschutz" className="text-sm text-white/50">
                      Es gilt unsere{' '}
                      <a href="/datenschutz" className="text-brand-400 underline hover:text-brand-300">
                        Datenschutzerklärung
                      </a>
                    </label>
                  </div>

                  {/* Submit */}
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                    whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                    className="w-full px-6 md:px-8 py-3 md:py-4 bg-white text-black text-xs md:text-sm uppercase tracking-[0.2em] font-bold rounded-full hover:bg-brand-800 hover:text-white transition-all duration-500 flex items-center justify-center gap-2 md:gap-3 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Wird gesendet...' : 'Nachricht senden'}
                    {!isSubmitting && <Send className="w-4 h-4" />}
                  </motion.button>

                  {/* Success Message */}
                  {submitStatus === 'success' && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 bg-brand-800/10 border border-brand-800/20 rounded-xl"
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-brand-400 flex-shrink-0" />
                        <div>
                          <p className="text-white font-medium mb-1">Vielen Dank!</p>
                          <p className="text-white/50 text-sm">Wir melden uns schnellstmöglich bei Ihnen.</p>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Error Message */}
                  {submitStatus === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5">⚠</div>
                        <div>
                          <p className="text-white font-medium mb-1">Fehler beim Versenden</p>
                          <p className="text-white/50 text-sm">Bitte versuchen Sie es später erneut oder kontaktieren Sie uns telefonisch.</p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          MAP SECTION
      ════════════════════════════════════════════════════════════ */}
      <section className="pb-24 md:pb-32 border-t border-white/5 pt-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-brand-800/10 border border-brand-800/20 mb-4">
                <MapPin className="w-5 h-5 text-brand-400" />
              </div>
              <h2 className="font-serif text-3xl md:text-4xl text-white font-light">
                Ihr Weg <span className="italic text-white/60">zu uns</span>
              </h2>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-2xl overflow-hidden border border-white/5"
          >
            <iframe
              src="https://www.google.com/maps?q=Hückelsmaystr.+70,+47804+Krefeld&output=embed"
              width="100%"
              height="400"
              className="w-full"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Standort Hückels May"
            />
          </motion.div>
        </div>
      </section>

    </div>
  )
}
